import { Box, Button, Image as ChakraImage, Flex, Heading, VStack } from '@chakra-ui/react';
import { Icon } from '@common/components/Icon';
import { Image } from '@common/components/Image';
import { Disclosure } from '@common/elements/Disclosure';
import React, { PropsWithChildren } from 'react';
import { useNavigate } from 'react-router-dom';
import { TextBlock } from 'src/apps/invest/common/components/TextBlock';
import { ROUTES } from 'src/apps/invest/config';
import { useSignUpModel } from '../../models/SignUpModel';

type UIProps = {
  prevPage?: string | (() => void);
  nextPage?: string | (() => void);
};

export const UI: React.FC<PropsWithChildren<UIProps>> = ({ prevPage, nextPage, children }) => {
  const navigate = useNavigate();
  const signUpModel = useSignUpModel();

  const handlePrevPage = () => {
    if (typeof prevPage === 'string') return navigate(prevPage);
    if (typeof prevPage === 'function') return prevPage();
  };

  const handleNextPage = () => {
    if (typeof nextPage === 'string') return navigate(nextPage);
    if (typeof nextPage === 'function') return nextPage();
  };

  return (
    <Flex bg="white" direction="column" h="100%" w="full" justifyContent={'space-between'}>
      {/* Navigation */}
      <Flex bg="white" justify={'center'} borderBottomWidth={1} borderBottomColor={'gray.50'}>
        <Flex
          maxW={920}
          mx={[5, 20]}
          width="full"
          direction="row"
          height={[70, 90]}
          color="white"
          alignItems={'center'}
          justifyContent="space-between">
          <Flex alignItems="flex-start">
            <Box as="button" onClick={handlePrevPage} w="30px">
              {prevPage && <Icon color="black" boxSize={['20px', '25px']} icon="arrow-left-long" />}
            </Box>
          </Flex>
          <Flex>
            <Image image="logo-black" w={[100, 150]} onClick={() => navigate(ROUTES.dashboard)} cursor="pointer" />
          </Flex>
          <Flex alignItems="flex-start">
            <Box as="button" onClick={handleNextPage} w="30px">
              {nextPage && <Icon color="black" boxSize={['20px', '25px']} icon="arrow-right-long" />}
            </Box>
          </Flex>
        </Flex>
      </Flex>

      {/* Body */}
      <Flex justifyContent="flex-start" direction={'column'} gap={5} py={['30px', '100px']} px={'30px'} height="100%">
        {/* Content */}
        <Flex flexDir={'column'} marginX={'auto'} maxWidth={920} width="full">
          <Flex direction="column" justifyContent="space-betweeen" width="full" minH={100}>
            {children}
            <Flex mt="19px" flex={['auto', 2]} justify={'center'} textAlign={['right', 'left']} direction="row" gap={2}>
              <Icon boxSize={3} color="gray.300" icon="lock"></Icon>
              <TextBlock color="gray.400" fontSize={'12px'}>
                Your info is secure and will never be shared
              </TextBlock>
            </Flex>
            {Boolean(signUpModel.partner) &&
              (Boolean(signUpModel.email) ||
                Boolean(signUpModel.firstName) ||
                Boolean(signUpModel.lastName) ||
                Boolean(signUpModel.phone)) &&
              signUpModel.page !== 'FINISH' && (
                <Flex w="100%" justify={'center'}>
                  <Flex
                    flexDir={['column', 'column', 'column', 'row']}
                    alignItems={'center'}
                    mt="33px"
                    py="8px"
                    px="25px"
                    backgroundColor="gray.50"
                    borderRadius={'25px'}
                    justify="center"
                    width={'fit-content'}>
                    <Flex justify="space-around" alignItems={'center'} flexDir={['column', 'column', 'column', 'row']}>
                      {Boolean(signUpModel.email) && (
                        <Flex px="10px" alignItems={'center'}>
                          <TextBlock fontSize={'12px'} opacity=".3" weight="bold">
                            {signUpModel.email}
                          </TextBlock>
                        </Flex>
                      )}
                      {(Boolean(signUpModel.firstName) || Boolean(signUpModel.lastName)) && (
                        <Flex px="10px" alignItems={'center'}>
                          <TextBlock fontSize={'12px'} opacity=".3" weight="bold">
                            {`${signUpModel.firstName}${signUpModel.lastName ? ` ${signUpModel.lastName}` : ''}`}
                          </TextBlock>
                        </Flex>
                      )}
                      {Boolean(signUpModel.phone) && (
                        <Flex px="10px" alignItems={'center'}>
                          <TextBlock fontSize={'12px'} opacity=".3" weight="bold">
                            {signUpModel.phone}
                          </TextBlock>
                        </Flex>
                      )}
                    </Flex>
                    <Box ml={['0px', '0px', '0px', '24px']}>
                      <Button
                        onClick={() => {
                          signUpModel.reset();
                        }}
                        fontSize="10px"
                        h="19px"
                        lineHeight={'19px'}
                        size="xs"
                        borderRadius={'15px'}
                        px="15px"
                        backgroundColor="gray.500"
                        color="gray.50">
                        Update
                      </Button>
                    </Box>
                  </Flex>
                </Flex>
              )}
            {signUpModel.partner && (
              <Flex mt="45px" justify={'center'} alignItems={'center'}>
                <TextBlock fontSize={'12px'} textTransform="uppercase" color={'gray.300'}>
                  {'In partnership with'}
                </TextBlock>
                {signUpModel.partner.style.secondary_logo?.download_url ? (
                  <ChakraImage h="32px" ml="10px" src={signUpModel.partner.style.secondary_logo?.download_url} />
                ) : (
                  <TextBlock fontSize="12px" ml="10px" color="gray.300">
                    {signUpModel.partner.name}
                  </TextBlock>
                )}
              </Flex>
            )}
          </Flex>
        </Flex>
      </Flex>

      {/* Footer */}
      <Flex
        backgroundColor="#263243"
        paddingX={5}
        direction={['column', 'row']}
        w="full"
        gap={1}
        align={['center', 'start']}>
        <VStack flex={['auto', 1]} px={['10px', '50px']} py="50px" color="white" fontSize={'0.8em'}>
          <Heading as="h3" fontSize="21px" color="white" fontWeight={'normal'} mb="20px">
            Important Disclosures
          </Heading>
          <Disclosure />
          <TextBlock color="white">
            © {new Date().getFullYear()} Concreit Inc. All Rights Reserved. Designed & developed in Seattle.
          </TextBlock>
        </VStack>
      </Flex>
    </Flex>
  );
};
